/**
 * WellDetailPage — Task 25.4 (DEC-115). PLATFORM_ADMIN well detail.
 *
 * Header (well name + API + operator + state/county/type/source/status +
 * Edit/Deactivate/Reactivate), a Well Info editor (well name + operator +
 * well status editable for MANUAL wells; GPS always editable; OCC/RRC name/operator
 * read-only), and a read-only Haulers section (pickup and/or disposal well, since when;
 * has tanks? in a battery? — OPEN-149).
 */
import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBackTo } from '../../lib/url-state';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { FormRow, fieldStyle, labelStyle, subText, Pill } from '../../ui/bits';
import { OperatorPicker } from './AddWellModal';
import { wellTypeLabel } from './well-type';
import {
  useWell,
  useUpdateWell,
  useDeactivateWell,
  useReactivateWell,
  type WellHaulerLink,
  type WellSummary,
} from './hooks';
import { formatNumber } from '@lodex-pro/shared/number-format';

const LINK_LABEL: Record<WellHaulerLink['kind'], string> = {
  PICKUP: 'Pickup well',
  DISPOSAL: 'Disposal well',
};

/** "Disposal well · since Oct 3, 2026" — local date (DEC-004). */
export function linkText(link: WellHaulerLink): string {
  const label = LINK_LABEL[link.kind];
  if (link.linkedAt === null) return label;
  const day = new Date(link.linkedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return `${label} · since ${day}`;
}

function Card({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
        marginBottom: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '14px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function ReadField({ label, value }: { label: string; value: string | null }): React.JSX.Element {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>
        {value !== null && value !== '' ? value : <span style={subText}>—</span>}
      </div>
    </div>
  );
}

function WellInfo({ well }: { well: WellSummary }): React.JSX.Element {
  const isManual = well.source === 'MANUAL';
  const [editing, setEditing] = React.useState(false);
  const [wellName, setWellName] = React.useState(well.wellName);
  const [operatorId, setOperatorId] = React.useState(well.operatorId ?? '');
  const [operatorName, setOperatorName] = React.useState(well.operatorName ?? '');
  const [wellStatus, setWellStatus] = React.useState(well.wellStatus ?? '');
  const [lat, setLat] = React.useState(well.wellheadLat ?? '');
  const [lng, setLng] = React.useState(well.wellheadLng ?? '');
  const [error, setError] = React.useState<string | null>(null);
  const update = useUpdateWell(well.id);

  const start = (): void => {
    setWellName(well.wellName);
    setOperatorId(well.operatorId ?? '');
    setOperatorName(well.operatorName ?? '');
    setWellStatus(well.wellStatus ?? '');
    setLat(well.wellheadLat ?? '');
    setLng(well.wellheadLng ?? '');
    setError(null);
    setEditing(true);
  };

  const save = (): void => {
    const latNum = lat.trim() === '' ? null : Number(lat);
    const lngNum = lng.trim() === '' ? null : Number(lng);
    if (latNum !== null && Number.isNaN(latNum)) return setError('Latitude must be a number.');
    if (lngNum !== null && Number.isNaN(lngNum)) return setError('Longitude must be a number.');
    setError(null);
    update.mutate(
      {
        // Name / operator / status are only editable for MANUAL wells; GPS always.
        ...(isManual && { wellName: wellName.trim() }),
        ...(isManual && operatorId !== '' && { operatorId }),
        ...(isManual && { wellStatus: wellStatus.trim() === '' ? null : wellStatus.trim() }),
        wellheadLat: latNum,
        wellheadLng: lngNum,
      },
      {
        onSuccess: () => {
          toast.success('Well updated');
          setEditing(false);
        },
        onError: () => setError('Could not save changes.'),
      },
    );
    return undefined;
  };

  if (!editing) {
    return (
      <Card
        title="Well Info"
        action={
          <Button type="button" variant="outline" onClick={start}>
            Edit
          </Button>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <ReadField label="Well name" value={well.wellName} />
          <ReadField label="Operator" value={well.operatorName} />
          <ReadField label="State" value={well.state} />
          <ReadField label="County" value={well.county} />
          <ReadField label="Well type" value={wellTypeLabel(well.wellType)} />
          <ReadField label="Well status" value={well.wellStatus} />
          <ReadField label="GPS latitude" value={well.wellheadLat} />
          <ReadField label="GPS longitude" value={well.wellheadLng} />
        </div>
        {!isManual && (
          <p style={{ ...subText, marginTop: '14px', textTransform: 'none' }}>
            Sourced from {well.source ?? 'OCC'} — name/operator are read-only; GPS is editable.
          </p>
        )}
      </Card>
    );
  }

  return (
    <Card title="Well Info">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
        <FormRow label="Well name" htmlFor="wi-name">
          {isManual ? (
            <input
              id="wi-name"
              value={wellName}
              onChange={(e): void => setWellName(e.target.value)}
              style={fieldStyle}
            />
          ) : (
            <div style={{ fontSize: '14px', paddingTop: '8px' }}>{well.wellName}</div>
          )}
        </FormRow>
        <FormRow label="Operator">
          {isManual ? (
            <OperatorPicker
              operatorId={operatorId}
              operatorName={operatorName}
              onSelect={(id, name): void => {
                setOperatorId(id);
                setOperatorName(name);
              }}
            />
          ) : (
            <div style={{ fontSize: '14px', paddingTop: '8px' }}>{well.operatorName ?? '—'}</div>
          )}
        </FormRow>
        <FormRow label="Well status" htmlFor="wi-status">
          {isManual ? (
            <input
              id="wi-status"
              value={wellStatus}
              maxLength={4}
              onChange={(e): void => setWellStatus(e.target.value.toUpperCase())}
              style={{ ...fieldStyle, maxWidth: '120px' }}
            />
          ) : (
            <div style={{ fontSize: '14px', paddingTop: '8px' }}>{well.wellStatus ?? '—'}</div>
          )}
        </FormRow>
        <div />
        <FormRow label="GPS latitude" htmlFor="wi-lat">
          <input
            id="wi-lat"
            value={lat}
            onChange={(e): void => setLat(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="GPS longitude" htmlFor="wi-lng">
          <input
            id="wi-lng"
            value={lng}
            onChange={(e): void => setLng(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
      </div>
      {error !== null && (
        <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)', marginTop: '12px' }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <Button
          type="button"
          disabled={update.isPending}
          onClick={save}
          style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        <Button type="button" variant="outline" onClick={(): void => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

export function WellDetailPage(): React.JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  // Back to the list as it was left: its search and filters (lib/url-state.ts).
  const backTo = useBackTo('/wells');
  const { data, isLoading, isError } = useWell(id);
  const deactivate = useDeactivateWell(id);
  const reactivate = useReactivateWell(id);

  if (isLoading) return <p style={{ ...subText, padding: '24px' }}>Loading well…</p>;
  if (isError || data === undefined) {
    return (
      <div style={{ padding: '24px' }}>
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load this well.
        </p>
        <Button type="button" variant="outline" onClick={(): void => void navigate(backTo)}>
          Back to wells
        </Button>
      </div>
    );
  }

  const { well, haulers } = data;

  const onDeactivate = (): void => {
    const msg =
      well.haulerCount > 0
        ? `Deactivate ${well.wellName}? ${formatNumber(well.haulerCount)} hauler(s) use it as a pickup or disposal well — their records are kept; the well shows inactive.`
        : `Deactivate ${well.wellName}?`;
    if (!window.confirm(msg)) return;
    deactivate.mutate(
      {},
      {
        onSuccess: () => toast.success('Well deactivated'),
        onError: () => toast.error('Could not deactivate'),
      },
    );
  };
  const onReactivate = (): void => {
    reactivate.mutate(
      {},
      {
        onSuccess: () => toast.success('Well reactivated'),
        onError: () => toast.error('Could not reactivate'),
      },
    );
  };

  return (
    <div style={{ padding: '24px', maxWidth: '960px', margin: '0 auto' }}>
      <button
        type="button"
        onClick={(): void => void navigate(backTo)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: 'none',
          color: 'var(--color-text-secondary)',
          cursor: 'pointer',
          fontSize: '13px',
          marginBottom: '12px',
          padding: 0,
        }}
      >
        <ArrowLeft size={16} aria-hidden="true" /> Wells
      </button>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
          gap: '16px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>{well.wellName}</h2>
            {well.state !== null && <Pill label={well.state} tone="neutral" />}
            {well.wellType !== null && (
              <Pill label={wellTypeLabel(well.wellType) ?? ''} tone="accent" />
            )}
            <Pill label={well.source ?? 'OCC'} tone="muted" />
            <Pill
              label={well.isActive ? 'Active' : 'Inactive'}
              tone={well.isActive ? 'positive' : 'muted'}
            />
          </div>
          <p style={{ ...subText, marginTop: '4px' }}>
            API {well.apiNumber}
            {well.operatorName !== null ? ` · ${well.operatorName}` : ''}
            {well.county !== null ? ` · ${well.county}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {well.isActive ? (
            <Button
              type="button"
              variant="outline"
              disabled={deactivate.isPending}
              onClick={onDeactivate}
              style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              type="button"
              disabled={reactivate.isPending}
              onClick={onReactivate}
              style={{
                backgroundColor: 'var(--color-accent-blue)',
                color: 'var(--color-on-accent)',
              }}
            >
              Reactivate
            </Button>
          )}
        </div>
      </div>

      <WellInfo well={well} />

      {/* Haulers */}
      <Card title={`Haulers (${haulers.length})`}>
        {haulers.length === 0 ? (
          <p style={{ ...subText, textTransform: 'none' }}>
            No haulers use this well as a pickup or disposal well.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {haulers.map((h) => (
              <div
                key={h.tenantId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--color-border-secondary)',
                  fontSize: '14px',
                }}
              >
                <span style={{ fontWeight: 500 }}>{h.tenantName}</span>
                {h.links.map((l) => (
                  <Pill key={l.kind} label={linkText(l)} />
                ))}
                {h.hasTanks && <Pill label="Has tanks" tone="accent" />}
                {h.inBattery && <Pill label="In tank battery" tone="accent" />}
              </div>
            ))}
          </div>
        )}
        <p style={{ ...subText, marginTop: '10px', textTransform: 'none' }}>
          Read-only. Deactivating the well does not change these hauler records.
        </p>
      </Card>
    </div>
  );
}
